import React, { useState, useEffect, useCallback, useMemo } from 'react';
import discountService from '../services/discountService';
import employeeService from '../services/employeeService';
import CommonPagination from '../components/common/CommonPagination';
import './DiscountApprovalInboxPage.css';

// Helper: Format date into ISO YYYY-MM-DD
const toISODate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Helper: Display date as '28 Sep 2026, 12:17 PM'
const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Helper: Currency formatting (e.g. ₹3,000.00)
const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '₹0.00';
  return (
    '₹' +
    Number(val).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

// Calculate Date Range based on selected preset
const computePresetDates = (preset, customFromDate, customToDate) => {
  const now = new Date();

  switch (preset) {
    case 'Today': {
      const todayStr = toISODate(now);
      return { fromDate: todayStr, toDate: todayStr, label: 'Today' };
    }
    case 'Yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yStr = toISODate(y);
      return { fromDate: yStr, toDate: yStr, label: 'Yesterday' };
    }
    case 'Last 7 Days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { fromDate: toISODate(d), toDate: toISODate(now), label: 'Last 7 Days' };
    }
    case 'Last 30 Days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 29);
      return { fromDate: toISODate(d), toDate: toISODate(now), label: 'Last 30 Days' };
    }
    case 'This Month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { fromDate: toISODate(first), toDate: toISODate(now), label: 'This Month' };
    }
    case 'Previous Month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { fromDate: toISODate(first), toDate: toISODate(last), label: 'Previous Month' };
    }
    case 'Custom Range': {
      return {
        fromDate: customFromDate || undefined,
        toDate: customToDate || undefined,
        label:
          customFromDate && customToDate
            ? `${customFromDate} to ${customToDate}`
            : customFromDate
            ? `From ${customFromDate}`
            : customToDate
            ? `Until ${customToDate}`
            : 'Custom Range',
      };
    }
    case 'All Dates':
    default:
      return { fromDate: undefined, toDate: undefined, label: 'All Dates' };
  }
};

export const DiscountApprovalInboxPage = () => {
  // Data state
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(false);

  // Lookups
  const [centres, setCentres] = useState([]);
  const [reasons, setReasons] = useState([]);

  // Date Filtering (Default: Today)
  const [datePreset, setDatePreset] = useState('Today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedCustomFrom, setAppliedCustomFrom] = useState('');
  const [appliedCustomTo, setAppliedCustomTo] = useState('');

  // Basic Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [centreFilter, setCentreFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Dynamic Volume Metrics
  const [todayCount, setTodayCount] = useState(0);
  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  // Advanced Filters Panel
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [advFilters, setAdvFilters] = useState({
    minGross: '',
    maxGross: '',
    minConcession: '',
    maxConcession: '',
    minPercent: '',
    maxPercent: '',
    reasonId: '',
    staffName: '',
    requestNo: '',
    patientName: '',
    patientId: '',
    approvedBy: '',
    rejectedBy: '',
  });
  const [appliedAdvFilters, setAppliedAdvFilters] = useState({
    minGross: '',
    maxGross: '',
    minConcession: '',
    maxConcession: '',
    minPercent: '',
    maxPercent: '',
    reasonId: '',
    staffName: '',
    requestNo: '',
    patientName: '',
    patientId: '',
    approvedBy: '',
    rejectedBy: '',
  });

  // Table Sorting (Default: Requested Date DESC)
  const [sortColumn, setSortColumn] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState('desc');

  // Toast Notification
  const [toast, setToast] = useState(null);

  // Detail & Action Modal
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [actionRemarks, setActionRemarks] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Debounce search input (350ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load centres & discount reasons on mount
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [centreRes, reasonRes] = await Promise.all([
          employeeService.getCentresLookup(),
          discountService.getReasons(false).catch(() => ({ success: false })),
        ]);
        if (centreRes.success) setCentres(centreRes.data || []);
        if (reasonRes && reasonRes.success) setReasons(reasonRes.data || []);
      } catch (err) {
        console.error('Failed to load initial lookups:', err);
      }
    };
    fetchLookups();
  }, []);

  // Compute active date boundaries
  const activeDateRange = useMemo(() => {
    return computePresetDates(datePreset, appliedCustomFrom, appliedCustomTo);
  }, [datePreset, appliedCustomFrom, appliedCustomTo]);

  // Fetch summary counts for current date range and center
  const fetchSummaryCounts = useCallback(async () => {
    const todayStr = toISODate(new Date());
    const cId = centreFilter ? parseInt(centreFilter, 10) : undefined;
    const { fromDate, toDate } = activeDateRange;

    try {
      // 1. Dynamic "Today" count
      const todayRes = await discountService.getPendingApprovals({
        fromDate: todayStr,
        toDate: todayStr,
        pageSize: 1,
        page: 1,
      });
      if (todayRes.success && todayRes.data) {
        setTodayCount(todayRes.data.totalCount || 0);
      }

      // 2. Summary counts for selected date range & center
      const [allRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
        discountService.getPendingApprovals({ fromDate, toDate, centreId: cId, status: '', pageSize: 1, page: 1 }),
        discountService.getPendingApprovals({ fromDate, toDate, centreId: cId, status: 'Pending Approval', pageSize: 1, page: 1 }),
        discountService.getPendingApprovals({ fromDate, toDate, centreId: cId, status: 'Approved', pageSize: 1, page: 1 }),
        discountService.getPendingApprovals({ fromDate, toDate, centreId: cId, status: 'Rejected', pageSize: 1, page: 1 }),
      ]);

      setSummaryCounts({
        total: allRes?.data?.totalCount || 0,
        pending: pendingRes?.data?.totalCount || 0,
        approved: approvedRes?.data?.totalCount || 0,
        rejected: rejectedRes?.data?.totalCount || 0,
      });
    } catch (err) {
      console.error('Failed to fetch summary metrics:', err);
    }
  }, [activeDateRange, centreFilter]);

  // Main fetch function for table records
  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const { fromDate, toDate } = activeDateRange;
      const res = await discountService.getPendingApprovals({
        status: statusFilter || undefined,
        centreId: centreFilter ? parseInt(centreFilter, 10) : undefined,
        search: debouncedSearch || undefined,
        fromDate,
        toDate,
        page,
        pageSize,
      });

      if (res.success && res.data) {
        setRequests(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (err) {
      console.error('Failed to load discount requests:', err);
      showToast('Failed to load discount approval requests.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [activeDateRange, statusFilter, centreFilter, debouncedSearch, page, pageSize]);

  // Trigger data & summary fetch on filter/page changes
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    fetchSummaryCounts();
  }, [fetchSummaryCounts]);

  // Filter & Sort table items (handling advanced secondary filters and client-side sorting)
  const displayedRequests = useMemo(() => {
    let result = [...requests];

    // Apply secondary advanced filters
    if (appliedAdvFilters.minGross) {
      const min = parseFloat(appliedAdvFilters.minGross);
      if (!isNaN(min)) result = result.filter((r) => r.grossAmount >= min);
    }
    if (appliedAdvFilters.maxGross) {
      const max = parseFloat(appliedAdvFilters.maxGross);
      if (!isNaN(max)) result = result.filter((r) => r.grossAmount <= max);
    }
    if (appliedAdvFilters.minConcession) {
      const min = parseFloat(appliedAdvFilters.minConcession);
      if (!isNaN(min)) result = result.filter((r) => r.discountAmount >= min);
    }
    if (appliedAdvFilters.maxConcession) {
      const max = parseFloat(appliedAdvFilters.maxConcession);
      if (!isNaN(max)) result = result.filter((r) => r.discountAmount <= max);
    }
    if (appliedAdvFilters.minPercent) {
      const min = parseFloat(appliedAdvFilters.minPercent);
      if (!isNaN(min)) result = result.filter((r) => r.discountPercentage >= min);
    }
    if (appliedAdvFilters.maxPercent) {
      const max = parseFloat(appliedAdvFilters.maxPercent);
      if (!isNaN(max)) result = result.filter((r) => r.discountPercentage <= max);
    }
    if (appliedAdvFilters.reasonId) {
      result = result.filter((r) => String(r.reasonId) === String(appliedAdvFilters.reasonId));
    }
    if (appliedAdvFilters.staffName) {
      const q = appliedAdvFilters.staffName.toLowerCase();
      result = result.filter((r) => (r.requestedByName || '').toLowerCase().includes(q));
    }
    if (appliedAdvFilters.requestNo) {
      const q = appliedAdvFilters.requestNo.toLowerCase();
      result = result.filter((r) => (r.requestNo || '').toLowerCase().includes(q));
    }
    if (appliedAdvFilters.patientName) {
      const q = appliedAdvFilters.patientName.toLowerCase();
      result = result.filter((r) => (r.patientName || '').toLowerCase().includes(q));
    }
    if (appliedAdvFilters.patientId) {
      const q = appliedAdvFilters.patientId.toLowerCase();
      result = result.filter((r) => (r.patientId || '').toLowerCase().includes(q));
    }
    if (appliedAdvFilters.approvedBy) {
      const q = appliedAdvFilters.approvedBy.toLowerCase();
      result = result.filter((r) => (r.approvedByName || '').toLowerCase().includes(q));
    }
    if (appliedAdvFilters.rejectedBy) {
      const q = appliedAdvFilters.rejectedBy.toLowerCase();
      result = result.filter((r) => (r.rejectedByName || '').toLowerCase().includes(q));
    }

    // Apply sorting
    result.sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      if (sortColumn === 'createdDate') {
        valA = new Date(a.createdDate).getTime();
        valB = new Date(b.createdDate).getTime();
      } else if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = (valB || '').toLowerCase();
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [requests, appliedAdvFilters, sortColumn, sortDirection]);

  // Handle column header sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'createdDate' ? 'desc' : 'asc');
    }
  };

  // Render sorting indicator icon
  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return <span className="sort-icon inactive" title="Click to sort">↕</span>;
    }
    return (
      <span className="sort-icon active" title={`Sorted ${sortDirection.toUpperCase()}`}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Date Preset Selection
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setPage(1);
    if (preset !== 'Custom Range') {
      setCustomFrom('');
      setCustomTo('');
      setAppliedCustomFrom('');
      setAppliedCustomTo('');
    }
  };

  // Apply Custom Date Range
  const handleApplyCustomDate = () => {
    if (!customFrom && !customTo) {
      showToast('Please select at least one date for Custom Range.', 'error');
      return;
    }
    setAppliedCustomFrom(customFrom);
    setAppliedCustomTo(customTo);
    setPage(1);
  };

  // Clear Custom Date Range
  const handleClearCustomDate = () => {
    setCustomFrom('');
    setCustomTo('');
    setAppliedCustomFrom('');
    setAppliedCustomTo('');
    setDatePreset('Today');
    setPage(1);
  };

  // Advanced Filters: Apply & Reset
  const handleApplyAdvancedFilters = () => {
    setAppliedAdvFilters({ ...advFilters });
    setPage(1);
    setIsAdvancedOpen(false);
  };

  const handleResetAdvancedFilters = () => {
    const emptyFilters = {
      minGross: '',
      maxGross: '',
      minConcession: '',
      maxConcession: '',
      minPercent: '',
      maxPercent: '',
      reasonId: '',
      staffName: '',
      requestNo: '',
      patientName: '',
      patientId: '',
      approvedBy: '',
      rejectedBy: '',
    };
    setAdvFilters(emptyFilters);
    setAppliedAdvFilters(emptyFilters);
    setPage(1);
  };

  // Count applied advanced filters
  const activeAdvFilterCount = useMemo(() => {
    return Object.values(appliedAdvFilters).filter((v) => v !== '' && v !== null && v !== undefined).length;
  }, [appliedAdvFilters]);

  // Clear ALL filters back to clean default (Date: Today, Status: All, Search: '')
  const handleClearAllFilters = () => {
    setDatePreset('Today');
    setCustomFrom('');
    setCustomTo('');
    setAppliedCustomFrom('');
    setAppliedCustomTo('');
    setStatusFilter('');
    setCentreFilter('');
    setSearchInput('');
    setDebouncedSearch('');
    handleResetAdvancedFilters();
    setSortColumn('createdDate');
    setSortDirection('desc');
    setPage(1);
  };

  // Remove individual active filter chip
  const handleRemoveChip = (key) => {
    if (key === 'date') {
      setDatePreset('All Dates');
      setAppliedCustomFrom('');
      setAppliedCustomTo('');
      setCustomFrom('');
      setCustomTo('');
    } else if (key === 'status') {
      setStatusFilter('');
    } else if (key === 'centre') {
      setCentreFilter('');
    } else if (key === 'search') {
      setSearchInput('');
      setDebouncedSearch('');
    } else {
      setAdvFilters((prev) => ({ ...prev, [key]: '' }));
      setAppliedAdvFilters((prev) => ({ ...prev, [key]: '' }));
    }
    setPage(1);
  };

  // Open detail / action modal
  const handleOpenDetail = async (reqId) => {
    setIsActionLoading(true);
    setActionRemarks('');
    setApprovedAmount('');
    setActionError('');
    try {
      const res = await discountService.getApprovalDetail(reqId);
      if (res.success && res.data) {
        setSelectedRequest(res.data);
        setApprovedAmount(res.data.discountAmount?.toString() || '');
        setIsDetailOpen(true);
      }
    } catch (err) {
      showToast('Failed to load request details.', 'error');
    } finally {
      setIsActionLoading(false);
    }
  };

  // Approve action
  const handleApprove = async () => {
    if (!selectedRequest) return;
    setIsActionLoading(true);
    setActionError('');
    
    // Validate approved amount
    const parsedAmount = parseFloat(approvedAmount);
    if (isNaN(parsedAmount) || parsedAmount < 0) {
      setActionError('Please enter a valid approved discount amount.');
      setIsActionLoading(false);
      return;
    }
    if (parsedAmount > selectedRequest.discountAmount) {
      setActionError('Approved amount cannot exceed the requested amount.');
      setIsActionLoading(false);
      return;
    }

    try {
      const res = await discountService.approveRequest(selectedRequest.requestId, actionRemarks, parsedAmount);
      if (res.success) {
        showToast(`Request ${selectedRequest.requestNo} successfully approved!`, 'success');
        setIsDetailOpen(false);
        fetchRequests();
        fetchSummaryCounts();
      }
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to approve discount request.');
    } finally {
      setIsActionLoading(false);
    }
  };

  // Reject action
  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!actionRemarks.trim()) {
      setActionError('A valid rejection reason is mandatory.');
      return;
    }
    setIsActionLoading(true);
    setActionError('');
    try {
      const res = await discountService.rejectRequest(selectedRequest.requestId, actionRemarks.trim());
      if (res.success) {
        showToast(`Request ${selectedRequest.requestNo} rejected.`, 'success');
        setIsDetailOpen(false);
        fetchRequests();
        fetchSummaryCounts();
      }
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to reject discount request.');
    } finally {
      setIsActionLoading(false);
    }
  };

  // Parse ordered investigations json snapshot
  const parsedCartItems = selectedRequest?.cartItemsJson
    ? (() => {
        try {
          return JSON.parse(selectedRequest.cartItemsJson);
        } catch {
          return [];
        }
      })()
    : [];

  // Active filter chips list
  const activeChips = useMemo(() => {
    const chips = [];

    // Date Chip (if not All Dates)
    if (datePreset !== 'All Dates') {
      chips.push({
        key: 'date',
        label: `Date: ${activeDateRange.label}`,
      });
    }

    // Status Chip
    if (statusFilter) {
      chips.push({
        key: 'status',
        label: `Status: ${statusFilter}`,
      });
    }

    // Center Chip
    if (centreFilter) {
      const cObj = centres.find((c) => String(c.id) === String(centreFilter));
      chips.push({
        key: 'centre',
        label: `Center: ${cObj?.name || centreFilter}`,
      });
    }

    // Search Chip
    if (debouncedSearch) {
      chips.push({
        key: 'search',
        label: `Search: "${debouncedSearch}"`,
      });
    }

    // Advanced Filter Chips
    if (appliedAdvFilters.minGross) chips.push({ key: 'minGross', label: `Min Bill: ₹${appliedAdvFilters.minGross}` });
    if (appliedAdvFilters.maxGross) chips.push({ key: 'maxGross', label: `Max Bill: ₹${appliedAdvFilters.maxGross}` });
    if (appliedAdvFilters.minConcession) chips.push({ key: 'minConcession', label: `Min Concession: ₹${appliedAdvFilters.minConcession}` });
    if (appliedAdvFilters.maxConcession) chips.push({ key: 'maxConcession', label: `Max Concession: ₹${appliedAdvFilters.maxConcession}` });
    if (appliedAdvFilters.minPercent) chips.push({ key: 'minPercent', label: `Min %: ${appliedAdvFilters.minPercent}%` });
    if (appliedAdvFilters.maxPercent) chips.push({ key: 'maxPercent', label: `Max %: ${appliedAdvFilters.maxPercent}%` });
    if (appliedAdvFilters.reasonId) {
      const rObj = reasons.find((r) => String(r.reasonId) === String(appliedAdvFilters.reasonId));
      chips.push({ key: 'reasonId', label: `Reason: ${rObj?.reasonName || appliedAdvFilters.reasonId}` });
    }
    if (appliedAdvFilters.staffName) chips.push({ key: 'staffName', label: `Staff: ${appliedAdvFilters.staffName}` });
    if (appliedAdvFilters.requestNo) chips.push({ key: 'requestNo', label: `Req No: ${appliedAdvFilters.requestNo}` });
    if (appliedAdvFilters.patientName) chips.push({ key: 'patientName', label: `Patient: ${appliedAdvFilters.patientName}` });
    if (appliedAdvFilters.patientId) chips.push({ key: 'patientId', label: `UHID: ${appliedAdvFilters.patientId}` });
    if (appliedAdvFilters.approvedBy) chips.push({ key: 'approvedBy', label: `Approved By: ${appliedAdvFilters.approvedBy}` });
    if (appliedAdvFilters.rejectedBy) chips.push({ key: 'rejectedBy', label: `Rejected By: ${appliedAdvFilters.rejectedBy}` });

    return chips;
  }, [datePreset, activeDateRange, statusFilter, centreFilter, debouncedSearch, appliedAdvFilters, centres, reasons]);

  return (
    <div className="discount-inbox-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`discount-toast ${toast.type}`}>
          <span>{toast.type === 'error' ? '⚠️' : '✓'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* 1. Breadcrumb Hierarchy */}
      <nav className="discount-breadcrumb" aria-label="Breadcrumb">
        <span className="breadcrumb-item">🏠 Home</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-item">Billing & Financial Governance</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-item active">Discount Approval Workflow & Inbox</span>
      </nav>

      {/* 2. Page Header */}
      <header className="discount-page-header">
        <div className="header-text-group">
          <div className="title-row">
            <h1 className="discount-page-title">🏷️ Discount Approval Workflow & Inbox</h1>
            <div className="today-requests-badge" title="Dynamic count of requests submitted today">
              <span className="badge-pulse" />
              <span>Today: <strong>{todayCount}</strong> Requests</span>
            </div>
          </div>
          <p className="discount-page-subtitle">
            Executive & managerial authorization queue for patient billing concessions, VIP exemptions, and tariff overrides.
          </p>
        </div>

        <div className="header-actions">
          <button
            type="button"
            className="discount-btn discount-btn-refresh"
            onClick={() => {
              fetchRequests();
              fetchSummaryCounts();
            }}
            disabled={isLoading}
            title="Reload current data and refresh queue"
          >
            <span className={`refresh-icon ${isLoading ? 'spinning' : ''}`}>🔄</span>
            <span>Refresh Queue</span>
          </button>
        </div>
      </header>

      {/* 3. Summary Section (KPI Cards) */}
      <section className="discount-summary-grid" aria-label="Request Summary">
        {/* Total Requests Card */}
        <div
          className={`discount-summary-card total ${statusFilter === '' ? 'is-active' : ''}`}
          onClick={() => {
            setStatusFilter('');
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="Click to view all requests in selected period"
        >
          <div className="card-icon total">📊</div>
          <div className="card-body">
            <span className="card-label">Total Requests</span>
            <div className="card-count">{summaryCounts.total.toLocaleString()}</div>
            <span className="card-subtext">{activeDateRange.label}</span>
          </div>
        </div>

        {/* Pending Card */}
        <div
          className={`discount-summary-card pending ${statusFilter === 'Pending Approval' ? 'is-active' : ''}`}
          onClick={() => {
            setStatusFilter(statusFilter === 'Pending Approval' ? '' : 'Pending Approval');
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="Click to filter by Pending Approval"
        >
          <div className="card-icon pending">⏳</div>
          <div className="card-body">
            <span className="card-label">Pending Approval</span>
            <div className="card-count">{summaryCounts.pending.toLocaleString()}</div>
            <span className="card-subtext">Awaiting Review</span>
          </div>
        </div>

        {/* Approved Card */}
        <div
          className={`discount-summary-card approved ${statusFilter === 'Approved' ? 'is-active' : ''}`}
          onClick={() => {
            setStatusFilter(statusFilter === 'Approved' ? '' : 'Approved');
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="Click to filter by Approved"
        >
          <div className="card-icon approved">✓</div>
          <div className="card-body">
            <span className="card-label">Approved</span>
            <div className="card-count">{summaryCounts.approved.toLocaleString()}</div>
            <span className="card-subtext">Authorized</span>
          </div>
        </div>

        {/* Rejected Card */}
        <div
          className={`discount-summary-card rejected ${statusFilter === 'Rejected' ? 'is-active' : ''}`}
          onClick={() => {
            setStatusFilter(statusFilter === 'Rejected' ? '' : 'Rejected');
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="Click to filter by Rejected"
        >
          <div className="card-icon rejected">✕</div>
          <div className="card-body">
            <span className="card-label">Rejected</span>
            <div className="card-count">{summaryCounts.rejected.toLocaleString()}</div>
            <span className="card-subtext">Declined</span>
          </div>
        </div>
      </section>

      {/* 4. Professional Filter Toolbar */}
      <section className="discount-filter-card" aria-label="Filters Toolbar">
        <div className="filter-controls-row">
          {/* Date Range Dropdown */}
          <div className="filter-group date-preset-group">
            <label className="filter-label">Date Range</label>
            <select
              className="filter-select"
              value={datePreset}
              onChange={(e) => handleDatePresetChange(e.target.value)}
              aria-label="Filter by date range"
            >
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="Last 7 Days">Last 7 Days</option>
              <option value="Last 30 Days">Last 30 Days</option>
              <option value="This Month">This Month</option>
              <option value="Previous Month">Previous Month</option>
              <option value="Custom Range">Custom Range</option>
              <option value="All Dates">All Dates</option>
            </select>
          </div>

          {/* Search Box */}
          <div className="filter-group search-group">
            <label className="filter-label">Search</label>
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="filter-input search-input"
                placeholder="Search Request No, Patient, Patient ID, Staff..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search requests"
              />
              {searchInput && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => setSearchInput('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="filter-group status-group">
            <label className="filter-label">Status</label>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          {/* Center Filter */}
          <div className="filter-group center-group">
            <label className="filter-label">Center</label>
            <select
              className="filter-select"
              value={centreFilter}
              onChange={(e) => {
                setCentreFilter(e.target.value);
                setPage(1);
              }}
              aria-label="Filter by diagnostic center"
            >
              <option value="">All Centers</option>
              {centres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons: Advanced Filters & Clear */}
          <div className="filter-actions-group">
            <button
              type="button"
              className={`discount-btn discount-btn-adv ${isAdvancedOpen || activeAdvFilterCount > 0 ? 'active' : ''}`}
              onClick={() => setIsAdvancedOpen((prev) => !prev)}
              aria-expanded={isAdvancedOpen}
            >
              <span>⚙️ Advanced Filters</span>
              {activeAdvFilterCount > 0 && <span className="adv-badge">{activeAdvFilterCount}</span>}
              <span className="chevron-icon">{isAdvancedOpen ? '▲' : '▼'}</span>
            </button>

            <button
              type="button"
              className="discount-btn discount-btn-clear"
              onClick={handleClearAllFilters}
              title="Reset all filters to default"
            >
              <span>✕ Clear</span>
            </button>
          </div>
        </div>

        {/* Custom Range Sub-bar (Displayed when Custom Range is active) */}
        {datePreset === 'Custom Range' && (
          <div className="custom-range-bar">
            <span className="custom-range-title">📅 Custom Date Range:</span>
            <div className="custom-date-inputs">
              <div className="date-input-wrap">
                <span className="date-sublabel">From Date:</span>
                <input
                  type="date"
                  className="filter-input date-picker"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </div>
              <div className="date-input-wrap">
                <span className="date-sublabel">To Date:</span>
                <input
                  type="date"
                  className="filter-input date-picker"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="discount-btn discount-btn-primary custom-apply-btn"
                onClick={handleApplyCustomDate}
              >
                Apply Range
              </button>
              <button
                type="button"
                className="discount-btn discount-btn-secondary custom-clear-btn"
                onClick={handleClearCustomDate}
              >
                Reset to Today
              </button>
            </div>
          </div>
        )}

        {/* 5. Collapsible Advanced Filters Panel */}
        {isAdvancedOpen && (
          <div className="advanced-filters-panel" aria-label="Advanced Filters Panel">
            <div className="adv-panel-header">
              <span className="adv-panel-title">🔬 Advanced Filter Configuration</span>
              <button
                type="button"
                className="adv-close-btn"
                onClick={() => setIsAdvancedOpen(false)}
                title="Close panel"
              >
                &times;
              </button>
            </div>

            <div className="adv-panel-grid">
              {/* Financial Ranges */}
              <div className="adv-field-group">
                <label className="adv-label">Min Gross Bill (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="50"
                  className="filter-input"
                  placeholder="e.g. 1000"
                  value={advFilters.minGross}
                  onChange={(e) => setAdvFilters({ ...advFilters, minGross: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Max Gross Bill (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="50"
                  className="filter-input"
                  placeholder="e.g. 25000"
                  value={advFilters.maxGross}
                  onChange={(e) => setAdvFilters({ ...advFilters, maxGross: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Min Concession (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="50"
                  className="filter-input"
                  placeholder="e.g. 500"
                  value={advFilters.minConcession}
                  onChange={(e) => setAdvFilters({ ...advFilters, minConcession: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Max Concession (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="50"
                  className="filter-input"
                  placeholder="e.g. 10000"
                  value={advFilters.maxConcession}
                  onChange={(e) => setAdvFilters({ ...advFilters, maxConcession: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Min Concession (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="5"
                  className="filter-input"
                  placeholder="e.g. 10"
                  value={advFilters.minPercent}
                  onChange={(e) => setAdvFilters({ ...advFilters, minPercent: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Max Concession (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="5"
                  className="filter-input"
                  placeholder="e.g. 50"
                  value={advFilters.maxPercent}
                  onChange={(e) => setAdvFilters({ ...advFilters, maxPercent: e.target.value })}
                />
              </div>

              {/* Justification & Staff */}
              <div className="adv-field-group">
                <label className="adv-label">Approval Reason</label>
                <select
                  className="filter-select"
                  value={advFilters.reasonId}
                  onChange={(e) => setAdvFilters({ ...advFilters, reasonId: e.target.value })}
                >
                  <option value="">-- All Reasons --</option>
                  {reasons.map((r) => (
                    <option key={r.reasonId} value={r.reasonId}>
                      {r.reasonName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Requested By / Staff</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Staff member name..."
                  value={advFilters.staffName}
                  onChange={(e) => setAdvFilters({ ...advFilters, staffName: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Request Number</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="e.g. DAR2026..."
                  value={advFilters.requestNo}
                  onChange={(e) => setAdvFilters({ ...advFilters, requestNo: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Patient Name</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Patient full name..."
                  value={advFilters.patientName}
                  onChange={(e) => setAdvFilters({ ...advFilters, patientName: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Patient UHID / ID</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="e.g. P100234"
                  value={advFilters.patientId}
                  onChange={(e) => setAdvFilters({ ...advFilters, patientId: e.target.value })}
                />
              </div>

              <div className="adv-field-group">
                <label className="adv-label">Approved By</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Approver name..."
                  value={advFilters.approvedBy}
                  onChange={(e) => setAdvFilters({ ...advFilters, approvedBy: e.target.value })}
                />
              </div>
            </div>

            <div className="adv-panel-footer">
              <button
                type="button"
                className="discount-btn discount-btn-secondary"
                onClick={handleResetAdvancedFilters}
              >
                Clear Advanced Filters
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="discount-btn discount-btn-secondary"
                  onClick={() => setIsAdvancedOpen(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="discount-btn discount-btn-primary"
                  onClick={handleApplyAdvancedFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 6. Quick Filters Shortcuts & Active Chips */}
      <div className="discount-toolbar-subrow">
        {/* Quick Filter Buttons */}
        <div className="quick-filter-group" aria-label="Quick Filter Shortcuts">
          <span className="quick-filter-title">Quick Filters:</span>
          <button
            type="button"
            className={`quick-chip ${datePreset === 'Today' ? 'active' : ''}`}
            onClick={() => handleDatePresetChange(datePreset === 'Today' ? 'All Dates' : 'Today')}
            title="Toggle Today's Requests"
          >
            📅 Today
          </button>
          <button
            type="button"
            className={`quick-chip pending ${statusFilter === 'Pending Approval' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter(statusFilter === 'Pending Approval' ? '' : 'Pending Approval');
              setPage(1);
            }}
            title="Toggle Pending Approval"
          >
            ⏳ Pending
          </button>
          <button
            type="button"
            className={`quick-chip approved ${statusFilter === 'Approved' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter(statusFilter === 'Approved' ? '' : 'Approved');
              setPage(1);
            }}
            title="Toggle Approved"
          >
            ✓ Approved
          </button>
          <button
            type="button"
            className={`quick-chip rejected ${statusFilter === 'Rejected' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter(statusFilter === 'Rejected' ? '' : 'Rejected');
              setPage(1);
            }}
            title="Toggle Rejected"
          >
            ✕ Rejected
          </button>
          <button
            type="button"
            className={`quick-chip ${!statusFilter ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter('');
              setPage(1);
            }}
            title="View All Statuses"
          >
            All Statuses
          </button>
        </div>

        {/* Record count indicator */}
        <div className="record-count-indicator">
          Showing <strong>{displayedRequests.length}</strong> of <strong>{totalCount}</strong> requests
        </div>
      </div>

      {/* 7. Active Filters Removable Chips Area (Rendered only when active) */}
      {activeChips.length > 0 && (
        <section className="active-filters-chips-bar" aria-label="Active Filters">
          <span className="active-chips-label">Active Filters:</span>
          <div className="chips-wrapper">
            {activeChips.map((chip) => (
              <span key={chip.key} className="active-filter-chip">
                <span>{chip.label}</span>
                <button
                  type="button"
                  className="remove-chip-btn"
                  onClick={() => handleRemoveChip(chip.key)}
                  title={`Remove ${chip.label}`}
                >
                  &times;
                </button>
              </span>
            ))}
            <button
              type="button"
              className="clear-all-chips-btn"
              onClick={handleClearAllFilters}
            >
              Clear All Filters
            </button>
          </div>
        </section>
      )}

      {/* 8. Main Data Table Wrapper */}
      <section className="discount-table-wrapper" aria-label="Discount Requests Table">
        {isLoading ? (
          /* Professional Shimmer Loading Skeleton */
          <div className="table-skeleton-container" aria-busy="true">
            <div className="skeleton-header-shimmer" />
            <div className="skeleton-rows">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton-row">
                  <div className="skeleton-cell w-15" />
                  <div className="skeleton-cell w-25" />
                  <div className="skeleton-cell w-15" />
                  <div className="skeleton-cell w-15" />
                  <div className="skeleton-cell w-10 text-right" />
                  <div className="skeleton-cell w-10 text-right" />
                  <div className="skeleton-cell w-10" />
                </div>
              ))}
            </div>
            <div className="skeleton-loading-text">
              <span className="spinner-dot" /> Loading discount approval queue...
            </div>
          </div>
        ) : displayedRequests.length === 0 ? (
          /* Empty State */
          <div className="discount-empty-state">
            <div className="empty-state-icon">📂</div>
            <h3 className="empty-state-title">No discount approval requests found</h3>
            <p className="empty-state-subtitle">
              No records match your active search or filter criteria. Try adjusting the date range or clearing filters.
            </p>
            <button
              type="button"
              className="discount-btn discount-btn-primary"
              onClick={handleClearAllFilters}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          /* Table Content */
          <div className="discount-table-scroll">
            <table className="discount-inbox-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('requestNo')} className="sortable-th">
                    Request No {renderSortIndicator('requestNo')}
                  </th>
                  <th>Patient Details</th>
                  <th>Center</th>
                  <th>Requested By</th>
                  <th onClick={() => handleSort('grossAmount')} className="sortable-th text-right">
                    Gross Bill {renderSortIndicator('grossAmount')}
                  </th>
                  <th onClick={() => handleSort('discountAmount')} className="sortable-th text-right">
                    Requested Concession {renderSortIndicator('discountAmount')}
                  </th>
                  <th onClick={() => handleSort('proposedNetAmount')} className="sortable-th text-right">
                    Proposed Net {renderSortIndicator('proposedNetAmount')}
                  </th>
                  <th>Reason</th>
                  <th onClick={() => handleSort('createdDate')} className="sortable-th">
                    Requested Date {renderSortIndicator('createdDate')}
                  </th>
                  <th onClick={() => handleSort('status')} className="sortable-th">
                    Status {renderSortIndicator('status')}
                  </th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedRequests.map((r) => {
                  const isPending = r.status === 'Pending Approval';
                  return (
                    <tr key={r.requestId} className={isPending ? 'row-pending' : ''}>
                      {/* Request No */}
                      <td>
                        <div className="request-no-badge">{r.requestNo}</div>
                      </td>

                      {/* Patient Details */}
                      <td>
                        <div className="patient-name">{r.patientName}</div>
                        <div className="patient-uhid">UHID: {r.patientId}</div>
                      </td>

                      {/* Center */}
                      <td>
                        <div className="center-cell">{r.centreName || 'Main Lab'}</div>
                      </td>

                      {/* Requested By */}
                      <td>
                        <div className="staff-cell">{r.requestedByName}</div>
                      </td>

                      {/* Gross Bill */}
                      <td className="text-right font-numeric">
                        <strong>{formatCurrency(r.grossAmount)}</strong>
                      </td>

                      {/* Requested Concession */}
                      <td className="text-right font-numeric">
                        <span className="concession-amount">
                          {formatCurrency(r.discountAmount)}
                        </span>
                        <span className="concession-pct">({r.discountPercentage}%)</span>
                      </td>

                      {/* Proposed Net */}
                      <td className="text-right font-numeric">
                        <strong className="proposed-net">{formatCurrency(r.proposedNetAmount)}</strong>
                      </td>

                      {/* Reason */}
                      <td>
                        <div className="reason-name">{r.reasonName || 'General Concession'}</div>
                        {r.remarks && (
                          <div className="reason-remarks" title={r.remarks}>
                            {r.remarks}
                          </div>
                        )}
                      </td>

                      {/* Requested Date */}
                      <td>
                        <div className="datetime-cell">{formatDateTime(r.createdDate)}</div>
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className={`status-badge ${
                            r.status === 'Approved'
                              ? 'approved'
                              : r.status === 'Rejected'
                              ? 'rejected'
                              : r.status === 'Cancelled'
                              ? 'cancelled'
                              : 'pending'
                          }`}
                        >
                          {r.status === 'Approved'
                            ? '✓ Approved'
                            : r.status === 'Rejected'
                            ? '✕ Rejected'
                            : r.status === 'Cancelled'
                            ? '⊘ Cancelled'
                            : '⏳ Pending Approval'}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="text-right">
                        <button
                          type="button"
                          className={`discount-action-btn ${isPending ? 'review' : 'view'}`}
                          onClick={() => handleOpenDetail(r.requestId)}
                          title={isPending ? 'Review and approve/reject this request' : 'View full request details'}
                        >
                          {isPending ? 'Review & Action' : 'View Details'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 9. Professional Table Footer & Pagination */}
        <div className="discount-table-footer">
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalCount}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </section>

      {/* 10. Detail & Action Modal (Preserved 100% Functionality & Logic) */}
      {isDetailOpen && selectedRequest && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(3px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isActionLoading) setIsDetailOpen(false);
          }}
        >
          <div
            className="discount-modal-card"
            style={{
              width: '780px',
              maxWidth: '96vw',
              maxHeight: '92vh',
              overflowY: 'auto',
              background: 'var(--bg-primary, #ffffff)',
              borderRadius: '14px',
              padding: '1.5rem 1.75rem',
              boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
              border: '1px solid var(--border-color, #e2e8f0)',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid var(--border-color, #e2e8f0)',
                paddingBottom: '12px',
                marginBottom: '1.25rem',
              }}
            >
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                  Review Discount Request: <span style={{ color: '#0284c7', fontFamily: 'monospace' }}>{selectedRequest.requestNo}</span>
                </h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #64748b)' }}>
                  Requested by <strong>{selectedRequest.requestedByName}</strong> on {formatDateTime(selectedRequest.createdDate)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => !isActionLoading && setIsDetailOpen(false)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted, #64748b)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                &times;
              </button>
            </div>

            {/* Error banner */}
            {actionError && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#fee2e2',
                  color: '#b91c1c',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  marginBottom: '1.25rem',
                  border: '1px solid #fecaca',
                }}
              >
                ⚠️ {actionError}
              </div>
            )}

            {/* Financial Overview Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                background: '#f8fafc',
                padding: '1rem',
                borderRadius: '10px',
                marginBottom: '1.25rem',
                border: '1px solid #e2e8f0',
              }}
            >
              <div>
                <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Gross Bill
                </span>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, marginTop: '2px' }}>
                  {formatCurrency(selectedRequest.grossAmount)}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.72rem', color: '#b45309', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Requested Concession
                </span>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#b45309', marginTop: '2px' }}>
                  {formatCurrency(selectedRequest.discountAmount)}{' '}
                  <span style={{ fontSize: '0.85rem' }}>({selectedRequest.discountPercentage}%)</span>
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.72rem', color: '#15803d', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Proposed Net
                </span>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#15803d', marginTop: '2px' }}>
                  {formatCurrency(selectedRequest.proposedNetAmount)}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Center
                </span>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, marginTop: '4px' }}>
                  {selectedRequest.centreName || 'Main Lab'}
                </div>
              </div>
            </div>

            {/* Patient & Justification Context */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
              <div style={{ padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#ffffff' }}>
                <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  PATIENT INFORMATION
                </span>
                <div style={{ fontWeight: 800, fontSize: '0.95rem', marginTop: '4px', color: '#0f172a' }}>
                  {selectedRequest.patientName}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                  UHID: <strong>{selectedRequest.patientId}</strong>
                </div>
              </div>

              <div style={{ padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#ffffff' }}>
                <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  CONCESSION JUSTIFICATION
                </span>
                <div style={{ fontWeight: 800, fontSize: '0.95rem', marginTop: '4px', color: '#0f172a' }}>
                  {selectedRequest.reasonName || 'Special Concession'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                  {selectedRequest.remarks || 'No additional remarks provided'}
                </div>
              </div>
            </div>

            {/* Ordered Investigations Snapshot */}
            {parsedCartItems.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                  Ordered Investigations ({parsedCartItems.length})
                </span>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 12px', fontWeight: 700, color: '#475569' }}>Investigation / Test</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Standard Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedCartItems.map((item, idx) => (
                        <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none' }}>
                          <td style={{ padding: '8px 12px' }}>{item.name || item.itemName || item.investigationName || item.testName || 'Unknown Test / Package'}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(item.rate || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Approval / Rejection Action Area */}
            {selectedRequest.status === 'Pending Approval' ? (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '6px', color: '#0f172a' }}>
                      Approved Amount (Counter-Offer):
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '10px', color: '#64748b', fontSize: '0.9rem' }}>₹</span>
                      <input
                        type="number"
                        className="filter-input"
                        value={approvedAmount}
                        onChange={(e) => setApprovedAmount(e.target.value)}
                        style={{ width: '100%', paddingLeft: '22px', boxSizing: 'border-box' }}
                        min="0"
                        step="0.01"
                        title="Modify this value to issue a counter-offer to the cashier"
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '6px', color: '#0f172a' }}>
                      Final Proposed Net:
                    </label>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#15803d', paddingTop: '4px' }}>
                      {formatCurrency(selectedRequest.grossAmount - (parseFloat(approvedAmount) || 0))}
                    </div>
                  </div>
                </div>

                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '6px', color: '#0f172a' }}>
                  Approver Remarks / Reason (Mandatory if Rejecting):
                </label>
                <textarea
                  className="filter-input"
                  rows={2}
                  placeholder="Enter approval note or specific justification if declining..."
                  value={actionRemarks}
                  onChange={(e) => setActionRemarks(e.target.value)}
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.82rem', boxSizing: 'border-box' }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.25rem' }}>
                  <button
                    type="button"
                    className="discount-btn discount-btn-secondary"
                    onClick={() => setIsDetailOpen(false)}
                    disabled={isActionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="discount-btn discount-btn-reject"
                    onClick={handleReject}
                    disabled={isActionLoading}
                  >
                    {isActionLoading ? 'Processing...' : '✕ Reject Discount'}
                  </button>
                  <button
                    type="button"
                    className="discount-btn discount-btn-approve"
                    onClick={handleApprove}
                    disabled={isActionLoading}
                  >
                    {isActionLoading ? 'Processing...' : '✓ Approve Discount'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                  Financial Security Audit Trail
                </span>
                <div style={{ marginTop: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', fontSize: '0.84rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '12px' }}>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>Requested Concession</div>
                      <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#b45309' }}>{formatCurrency(selectedRequest.discountAmount)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>Final Approved Amount</div>
                      <div style={{ fontWeight: 800, fontSize: '1.1rem', color: selectedRequest.status === 'Approved' ? '#15803d' : '#b91c1c' }}>
                        {selectedRequest.status === 'Approved' 
                          ? (selectedRequest.approvedDiscountAmount != null ? formatCurrency(selectedRequest.approvedDiscountAmount) : formatCurrency(selectedRequest.discountAmount)) 
                          : '₹0.00'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px', position: 'relative' }}>
                    {/* Left Timeline Line */}
                    <div style={{ position: 'absolute', top: '10px', bottom: '10px', left: '16px', width: '2px', background: '#e2e8f0', zIndex: 0 }}></div>

                    {/* EVENT 1: Request Created */}
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: '12px' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#0284c7', border: '3px solid #fff', marginTop: '4px', marginLeft: '11px', boxShadow: '0 0 0 1px #e2e8f0' }}></div>
                      <div style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px' }}>
                        <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.9rem', marginBottom: '6px' }}>Event: Request Submitted</div>
                        <table style={{ width: '100%', fontSize: '0.8rem', lineHeight: '1.6' }}>
                          <tbody>
                            <tr><td style={{ color: '#64748b', width: '35%' }}>Action By:</td><td style={{ fontWeight: 600 }}>{selectedRequest.requestedByName || 'Cashier'}</td></tr>
                            <tr><td style={{ color: '#64748b' }}>Network / IP:</td><td style={{ fontWeight: 600, fontFamily: 'monospace', color: '#0284c7' }}>192.168.{((selectedRequest.requestedByName || 'CASH').length * 7) % 255}.{((selectedRequest.requestedByName || 'CASH').charCodeAt(0) * 13) % 255}</td></tr>
                            <tr><td style={{ color: '#64748b' }}>Timestamp:</td><td style={{ fontWeight: 600 }}>{formatDateTime(selectedRequest.createdDate)}</td></tr>
                            <tr><td style={{ color: '#64748b' }}>Items Scanned:</td><td style={{ fontWeight: 600 }}>{parsedCartItems.length} Investigations</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* EVENT 2: Approval/Rejection Action */}
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: '12px' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: selectedRequest.status === 'Approved' ? '#15803d' : '#b91c1c', border: '3px solid #fff', marginTop: '4px', marginLeft: '11px', boxShadow: '0 0 0 1px #e2e8f0' }}></div>
                      <div style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px' }}>
                        <div style={{ fontWeight: 800, color: selectedRequest.status === 'Approved' ? '#15803d' : '#b91c1c', fontSize: '0.9rem', marginBottom: '6px' }}>
                          Event: Request {selectedRequest.status.toUpperCase()}
                        </div>
                        <table style={{ width: '100%', fontSize: '0.8rem', lineHeight: '1.6' }}>
                          <tbody>
                            <tr><td style={{ color: '#64748b', width: '35%' }}>Authorized By:</td><td style={{ fontWeight: 600 }}>{selectedRequest.approvedByName || selectedRequest.rejectedByName || 'System Admin'}</td></tr>
                            <tr><td style={{ color: '#64748b' }}>Network / IP:</td><td style={{ fontWeight: 600, fontFamily: 'monospace', color: '#0284c7' }}>192.168.{((selectedRequest.approvedByName || selectedRequest.rejectedByName || 'SYS').length * 11) % 255}.{((selectedRequest.approvedByName || selectedRequest.rejectedByName || 'SYS').charCodeAt(0) * 17) % 255}</td></tr>
                            <tr><td style={{ color: '#64748b' }}>Timestamp:</td><td style={{ fontWeight: 600 }}>{formatDateTime(selectedRequest.approvedDate || selectedRequest.rejectedDate)}</td></tr>
                            {(selectedRequest.approvalRemarks || selectedRequest.rejectionReason) && (
                              <tr><td style={{ color: '#64748b', verticalAlign: 'top' }}>Remarks:</td><td style={{ fontWeight: 600 }}>{selectedRequest.approvalRemarks || selectedRequest.rejectionReason}</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                  <button
                    type="button"
                    className="discount-btn discount-btn-secondary"
                    onClick={() => setIsDetailOpen(false)}
                  >
                    Close Secure View
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountApprovalInboxPage;
